import React, { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { usePasswordConstraints } from '../../../hooks/use-password-constraints';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';

enum Status {
  Idle,
  InProgress,
  Success,
  Error,
}

type Form = {
  password: string;
};

type Props = {
  onClose: (state: boolean) => void;
};

/**
 * Form verifies both user created from /registercompany and "Invite user".
 * Consider one API endpoint when moving from API v0 to v1.
 */
const DisconnectSsoDialog = (props: Props) => {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState(Status.Idle);

  const {
    register,
    getValues,
    watch,
    formState: { errors, isValidating },
  } = useForm<Form>({
    defaultValues: {
      password: '',
    },
  });

  const passwordConstraints = usePasswordConstraints({ password: watch('password') });

  // Set status to Idle to remove any errors (Status.Error) when revalidating the form.
  React.useEffect(() => {
    if (isValidating) {
      setStatus(Status.Idle);
    }
  }, [isValidating]);

  const disconnectProfileSso = async (event: FormEvent) => {
    try {
      event.preventDefault();

      setStatus(Status.InProgress);

      const profileV1Service = SkyMapAxiosServiceFactory.instance.createProfileServiceV1();
      await profileV1Service.deleteActiveSsoConnection({
        body: { password: getValues('password') },
      });

      setStatus(Status.Success);
      props.onClose(true);
    } catch (err) {
      setStatus(Status.Error);
    }
  };

  return (
    <Dialog
      closeIcon={status !== Status.InProgress}
      closeOnDimmerClick={status !== Status.InProgress}
      useGoogleTranslate={false}
      width={500}
      onClose={() => props.onClose(false)}
    >
      {{
        header: t('sections.authentication.disableSSODialog.header', { ns: 'profile' }),
        content: (
          <Form onSubmit={disconnectProfileSso}>
            <InfoBox bottomMargin={true} color="yellow">
              {t('sections.authentication.disableSSODialog.info', { ns: 'profile' })}
            </InfoBox>

            <LabelledContainer required={true} text={t('password', { ns: 'profile' })}>
              {(formElementId) => (
                <>
                  <TextBox
                    {...register('password', {
                      required: true,
                      validate: () => passwordConstraints.isValid,
                    })}
                    disabled={status === Status.InProgress}
                    errorMessage={errors.password?.message}
                    id={formElementId()}
                    maxLength={255}
                    type="password"
                  />
                  {passwordConstraints.render()}
                </>
              )}
            </LabelledContainer>

            {status === Status.Error && (
              <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
                {t('unexpectedError', { ns: 'common' })}
              </InfoBox>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
              <Button
                color="primary"
                loading={status === Status.InProgress}
                type="submit"
                variant={'contained'}
              >
                {t('sections.authentication.disableSSODialog.submitFormButton', { ns: 'profile' })}
              </Button>
            </Stack>
          </Form>
        ),
      }}
    </Dialog>
  );
};

// TODO: Investigate how to expose event handlers based on prop "as" in Stack component:
// TODO: <Stack as="form" onSubmit="...">...</Stack>
// TODO: For now, style form with inline flex CSS.
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

export { DisconnectSsoDialog };
